<script>
	import { onMount } from "svelte";
	import { claimsHistoryState } from "../../state/ApplicationState.svelte";
	import {
		FormGroup,
		Input,
		Modal,
		ModalHeader,
		ModalBody,
	} from "@sveltestrap/sveltestrap";
	import {
		debouncePromise,
		baseUrl,
		getDateFormatter,
		isValidDate,
	} from "../../../../../SharedSvelte/Common.svelte";
	import { Loader } from "../../../../../SharedSvelte/GlobalLoader.svelte";
	import { ClaimRecord } from "./ClaimHistoryRecord.svelte";
	import ClaimHistoryRecord from "./ClaimHistoryRecord.svelte";
	import Dialog from "../../../../../SharedSvelte/Dialog.svelte";

	let errors = $state({});
	let isQueryComplete = $state(false);
	let loadError = $state("");
	let selectedClaimDocuments = $state([]);
	let multipleEobsIsOpen = $state(false);
	let prompt;

	class ClaimDocument {
		constructor(data, claimId, digest) {
			this.claimId = claimId;
			this.digest = digest;
			this.entryId = data.entryId;
			this.name = data.name;
		}
	}

	const toggleMultipleEobs = () => {
		//console.log("togglingMultipleEobs");
		multipleEobsIsOpen = !multipleEobsIsOpen;
	};

	const loadHistory = async () => {
		//console.log("load history");
		isQueryComplete = false;
		Loader.display();
		claimsHistoryState.claimsList = [];
		claimsHistoryState.hasAppeals = false;
		loadError = "";

		try {  
			
			const payload = {
				memberId: claimsHistoryState.memberId,
				claimType: claimsHistoryState.claimType,
				beginningDate: claimsHistoryState.beginningDate,   
				endingDate: claimsHistoryState.endingDate,
			};
			
			//console.log(payload);
			const isValid = Object.values(payload).every(v => v != null); // true if none are null or undefined
			if (!isValid) return null;

			const response = await fetch(
				`${baseUrl}Options/Claims/QueryHistory`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						__RequestVerificationToken: document.getElementsByName("__RequestVerificationToken")[0].value
					},
					body: JSON.stringify(payload),
				},
			);
			if (!response.ok) {
				const error = `Server exception: ${response.status} ${response.statusText}`;
				console.log(error);
				throw new Error();
			}

			const json = await response.json();
			if (!json.success) {
				//console.log(json.errors);
				throw new Error(json.errors);
			}
			
			return json;

		} catch (err) {
			loadError = "Failed to load claims. ";
			if (err.message) loadError += err.message;
		} finally {
			isQueryComplete = true;
			Loader.hide();
		}
	};

	async function loadEob(data) {
		//console.log("loadEob", data);
		Loader.display();

		try {
			const response = await fetch(
				`${baseUrl}Options/Claims/ListClaimDocuments?claimId=${data.claimId}&digest=${data.digest}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						__RequestVerificationToken: document.getElementsByName("__RequestVerificationToken")[0].value
					},
				},
			);

			if (!response.ok) {
				const error = `Server exception: ${response.status} ${response.statusText}`;
				//prompt.showMessage(error, "Information");
				throw new Error(error);
			}

			const json = await response.json();

			if (json.success) {
				selectedClaimDocuments = [];
				const docs = json.documents;
				if (docs && docs.length === 1) {
					const doc = docs[0];
					const url = `${baseUrl}Options/Claims/DownloadEob?entryId=${encodeURIComponent(doc.entryId)}&claimId=${data.claimId}&digest=${data.digest}`;
					window.open(url, "_blank", "noopener,noreferrer");
				} else if (docs && docs.length > 1) {
					//selectedClaimDocuments = json.documents?.map((d) => new ClaimDocument(d, data.claimId, data.digest));
					for (var i = 0; i < docs.length; i++) {
						var d = docs[i];
						//console.log("adding document", d.claimId, d.entryId);
						selectedClaimDocuments.push(new ClaimDocument(d, data.claimId, data.digest));
					}
					
					//show Modal...
					multipleEobsIsOpen = true;
				} else if (!docs || docs.length < 1) {
					prompt.showMessage(
						"This EOB is currently unavailable.",
						"Information",
					);
				}
			} else
				prompt.showMessage(
					"Cannot get documents due to server condition.",
					"Information",
				);

			return json; // Resolves the Promise with the fetched data
		} catch (error) {
			console.error("Error downloading EOB:", error);
			prompt.showMessage("Error downloading EOB.", "Information");
			return Promise.reject(error); // Rejects the Promise with the error
		} finally {
			Loader.hide();
		}
	}

	function downloadEobFromClaimDocuments(data) {
		const url = `${baseUrl}Options/Claims/DownloadEob?entryId=${encodeURIComponent(data.entryId)}&claimId=${data.claimId}&digest=${data.digest}`;
		window.open(url, "_blank", "noopener,noreferrer");
	}

	function downloadClaimsCsv() {
		const url = `${baseUrl}Options/Claims/DownloadCsv?memberId=${claimsHistoryState.memberId}&claimType=${claimsHistoryState.claimType}&beginningDate=${claimsHistoryState.beginningDate}&endingDate=${claimsHistoryState.endingDate}`;
		window.open(url, "_blank", "noopener,noreferrer");
	}

	function displayResults(data) {
		if (!data?.claims) return;
		claimsHistoryState.claimsList = data.claims?.map((d) => new ClaimRecord(d));
		claimsHistoryState.hasAppeals = data.hasAppeals;
	}

	function handleFilterSelectChange(event) {
		doQuery().then((data) => displayResults(data));
	}

	function handleFilterDateChange(event) {
		function validateDateByKey(stateKey) {
			Reflect.deleteProperty(errors, stateKey);
			const invalidDateMessage = "Invalid Date";
			try {
				const dateFormatter = getDateFormatter();
				const rawValue = claimsHistoryState[stateKey];
				if (!rawValue) {
					errors[stateKey] = invalidDateMessage;
					return;
				}

				const date = new Date(rawValue);
				if (Number.isNaN(date.getTime())) {
					errors[stateKey] = invalidDateMessage;
					return;
				}

				const formatted = dateFormatter.format(date);
				if (!isValidDate(formatted)) {
					errors[stateKey] = invalidDateMessage;
				}
			} catch {
				errors[stateKey] = invalidDateMessage;
			}

			if (errors.beginningDate || (errors.endingDate && errors.endingDate === invalidDateMessage)) {
				return;
			}

			const endDate = new Date(claimsHistoryState.endingDate);
			const beginDate = new Date(claimsHistoryState.beginningDate);
			if (endDate < beginDate) {
				errors.endingDate = "Ending Date cannot be before Beginning Date.";
				return;
			} else {
				Reflect.deleteProperty(errors, "endingDate");
			}
		}

		const inputName = event.target.name;

		validateDateByKey(
			inputName === "EndingDate" ? "endingDate" : "beginningDate",
		);
		
		if (Object.keys(errors).length === 0) {
			doQuery().then((data) => displayResults(data));
		}
	}

	function handleDownloadEob(data) {
		//console.log("handle download eob", data);
		if (data.isHistoricalEob) {
			const url = `${baseUrl}Options/Claims/DownloadEob?entryId=${encodeURIComponent(data.documentEntryId)}&claimId=${data.claimId}&digest=${data.digest}`;
			window.open(url, "_blank", "noopener,noreferrer");
		} else {
			loadEob(data);
		}
	}

	const doQuery = debouncePromise(loadHistory, 500);
	//const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	onMount(async () => {
		//trigger initial history load...
		loadHistory().then((data) => displayResults(data));

	});
</script>

<main>
	<!-- <pre>{JSON.stringify(claimsHistoryState)}</pre> -->
	<!-- <pre>{JSON.stringify(errors)}</pre> -->
	<pre class="hidden">{JSON.stringify(selectedClaimDocuments)}</pre>
	<pre class="hidden">{JSON.stringify(multipleEobsIsOpen)}</pre>

	<div class="row mb-3 search-row">
		<div class="col-md-3 col-sm-3 col-xs-12">
			<FormGroup floating spacing="mb-3" label="Member">
				<Input
					type="select"
					bind:value={claimsHistoryState.memberId}
					onchange={handleFilterSelectChange}
				>
					{#each claimsHistoryState.memberOptions as memberOption}
						<option value={memberOption.key}
							>{memberOption.value}</option
						>
					{/each}
				</Input>
			</FormGroup>
		</div>

		<div class="col-md-3 col-sm-3 col-xs-12">
			<FormGroup floating spacing="mb-3" label="Type">
				<Input
					type="select"
					bind:value={claimsHistoryState.claimType}
					onchange={handleFilterSelectChange}
				>
					{#each claimsHistoryState.claimTypeOptions as typeOption}
						<option value={typeOption.key}
							>{typeOption.value}</option
						>
					{/each}
				</Input>
			</FormGroup>
		</div>

		<div class="col-md-3 col-sm-3 col-xs-12">
			<FormGroup floating spacing="mb-3" label="Beginning Date">
				<Input
					placeholder="MM/DD/YYYY"
					name="BeginningDate"
					type="date"
					bind:value={claimsHistoryState.beginningDate}
					onchange={handleFilterDateChange}
					invalid={Boolean(errors.beginningDate)}
					feedback={errors.beginningDate}
				/>
			</FormGroup>
		</div>

		<div class="col-md-3 col-sm-3 col-xs-12">
			<FormGroup floating spacing="mb-3" label="Ending Date">
				<Input
					placeholder="MM/DD/YYYY"
					name="EndingDate"
					type="date"
					bind:value={claimsHistoryState.endingDate}
					onchange={handleFilterDateChange}
					invalid={Boolean(errors.endingDate)}
					feedback={errors.endingDate}
				/>
			</FormGroup>
		</div>
	</div>
	{#if loadError}
		<div class="row">
			<div class="col text-danger">{loadError}</div>
		</div>
	{/if}

	{#if isQueryComplete}
		{#if claimsHistoryState.claimsList?.length > 0}
				<div class="infobox-claims">
					<p>
						<i class="fa fa-info-circle"></i>
						<strong
							>The overview below is just a summary of claims we’ve
							received and may not reflect your true financial
							obligation. Click on the individual claim for detailed
							information.</strong
						>
					</p>
				</div>

			<h5 class="mb-3">
				<button
					type="button"
					class="btn btn-link d-print-none"
					onclick={downloadClaimsCsv}
					><i class="excel-icon"></i>Download Claims History</button
				>
			</h5>
	<div class="table-responsive">
			<table class="table table-hover table-bordered table-striped mb-4">
				<thead>
					<tr class="table-primary fw-bold">
						<td></td>
						<td>Service Date</td>
						<td>Process Date</td>
						<td class="text-center">EOB</td>
						<td>Provider</td>
						<td class="text-nowrap">Patient Name</td>
						<td class="text-end">Total Patient Responsibility</td>
						<td>Status</td>
						{#if claimsHistoryState.hasAppeals}
							<td>Appealed</td>
						{/if}
					</tr>
				</thead>
				<tbody>
					{#each claimsHistoryState.claimsList as item}
						<ClaimHistoryRecord
							claimRecord={item}
							hasAnyAppeals={claimsHistoryState.hasAppeals}
							onDownloadEob={handleDownloadEob}
						></ClaimHistoryRecord>
					{/each}
				</tbody>
			</table>
	</div>
		{:else}
			<h4 class="mb-3 mt-3">
				There are no claims for the currently selected member, type,
				beginning date and ending date.
			</h4>
		{/if}
	{/if}
</main>

<Dialog bind:this={prompt} />

<Modal isOpen={multipleEobsIsOpen} toggle={toggleMultipleEobs}>
	<ModalHeader toggle={toggleMultipleEobs}>Multiple EOB Files</ModalHeader>
	<ModalBody>
		<div class="alert alert-info">
			Please select from the files below.
		</div>

		<div class="row mb-3">
			<table class="table table-striped">
				<tbody>
					{#each selectedClaimDocuments as doc}
						<tr>
							<td onclick={() => downloadEobFromClaimDocuments(doc)}
								class="hover-hand">{doc.name}</td>
							<td onclick={() => downloadEobFromClaimDocuments(doc)}
								class="hover-hand"
								><i class="pdf-lg"></i></td
							>
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
	</ModalBody>
	<!-- <ModalFooter>
		<Button color="secondary" onclick={toggleMultipleEobs}>Close</Button>
	</ModalFooter> -->
</Modal>